
import { Box, MenuItem, TextField } from '@mui/material'
import { BoxFC, BoxFR } from 'components/BoxCustom'
import ComboBox from 'components/ComboBox'
import DataGridCellExpand from 'components/DataGridCellExpand/DataGridCellExpand'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { GlobalStateContext } from 'contexts/GlobalStateContext'
import SearchButton from 'components/buttons/SearchButton'
import ClearButton from 'components/buttons/ClearButton'
import AddButton from 'components/buttons/AddButton'
import { DesktopDatePicker } from '@mui/x-date-pickers-pro'
import { blue, red } from '@mui/material/colors'
import { addIdForDataGrid } from 'utils'
import { addSumRow, getSumColorClassName, sxSumColorClass } from 'utils/highlightSumColor'
import { clearTruckFixCostFilterData, initTruckFixCostFilterData } from './initData'
import { truckFixCostColumns } from './columns'
import { truckFixCostColNm } from './constant'
import { truckColNm } from 'constants/truckConstant'
import TruckFixCostDialog from './TruckFixCostDialog'

let selectedId = null
let lastFilter = null

const TruckFixCost = () => {
  const { ax, msData } = useContext(GlobalStateContext)
  const [dataTable, setDataTable] = useState([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [filterData, setFilterData] = useState({ ...initTruckFixCostFilterData })
  // Types are data (truck_fix_cost_type) — a branch can add its own, so never hardcode.
  const [typCombo, setTypCombo] = useState([])

  const getData = useCallback((filter) => {
    ax.post("/truck-fix-cost/getTruckFixCost", filter).then(value => {
      if (value.data) {
        setDataTable(addIdForDataGrid(value.data, 'TukFixCostId'))
        lastFilter = filter
      }
    })
  }, [ax])

  // Amnt is the whole-period total and MonthAmnt the slice — summing both makes the
  // difference between "committed" and "this month's cost" readable at a glance.
  const modDataTable = useMemo(() => addSumRow({
    dataTable,
    sumColNms: ["Amnt", "ItmAmnt", "MonthAmnt", "NumVeh"],
    sumColTotal: "TukFixCostTypNm",
  }), [dataTable])

  const handleCellClick = useCallback(({ field, id }) => {
    if (field === 'ShowDetail' && id !== 0) {
      selectedId = id
      setDialogOpen(true)
    }
  }, [])

  const handleRowDoubleClick = useCallback(({ id }) => {
    if (id === 0) return
    selectedId = id
    setDialogOpen(true)
  }, [])

  const handleAdd = useCallback(() => {
    selectedId = null
    setDialogOpen(true)
  }, [])

  const onFinish = useCallback(() => {
    getData(lastFilter || filterData)
  }, [getData, filterData])

  const loadTypCombo = useCallback(() => {
    ax.post("/truck-fix-cost/getTruckFixCostType", { IsActv: 1 }).then(value => {
      if (value.data) {
        setTypCombo(value.data.map(t => ({
          id: t.TukFixCostTypId, label: t.TukFixCostTypNm,
        })))
      }
    })
  }, [ax])

  useEffect(() => {
    getData({ ...initTruckFixCostFilterData })
    loadTypCombo()
  }, [getData, loadTypCombo])

  return (
    <BoxFC height='100%'>
      <BoxFR>
        <AddButton onClick={handleAdd}>เพิ่มรายการ</AddButton>
        <Box flex={1} />
        <ComboBox
          options={msData.truckCombo || []}
          label={truckColNm.Code}
          selectedId={filterData.TukId}
          setSelectedId={(id) => setFilterData(o => ({ ...o, TukId: id }))}
          sx={{ width: 130 }}
        />
        <ComboBox
          options={msData.trailerCombo || []}
          label="หางพ่วง"
          selectedId={filterData.TlrId}
          setSelectedId={(id) => setFilterData(o => ({ ...o, TlrId: id }))}
          sx={{ width: 130 }}
        />
        <TextField size="small" label={truckFixCostColNm.TukFixCostTypId} select sx={{ width: 150 }}
          value={filterData.TukFixCostTypId ?? ""}
          onChange={(e) => setFilterData(o => ({ ...o, TukFixCostTypId: e.target.value || null }))}>
          <MenuItem value="">ทั้งหมด</MenuItem>
          {typCombo.map(t => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
        </TextField>
        <TextField size="small" label={truckFixCostColNm.IsActv} select sx={{ width: 100 }}
          value={filterData.IsActv ?? ""}
          onChange={(e) => setFilterData(o => ({ ...o, IsActv: e.target.value === "" ? null : +e.target.value }))}>
          <MenuItem value="">ทั้งหมด</MenuItem>
          <MenuItem value={1}>ใช้งาน</MenuItem>
          <MenuItem value={0}>ยกเลิก</MenuItem>
        </TextField>
        <DesktopDatePicker
          openTo="month"
          views={["year", "month"]}
          disableMaskedInput
          label={truckFixCostColNm.MonthAmnt}
          inputFormat="MMMM YYYY"
          value={filterData.MonthDte}
          onChange={(v) => setFilterData(o => ({ ...o, MonthDte: v }))}
          renderInput={(params) => <TextField size="small" {...params} sx={{ width: 160 }} />}
        />
        <SearchButton onClick={() => getData(filterData)} />
        <ClearButton onClick={() => setFilterData({ ...clearTruckFixCostFilterData })} />
      </BoxFR>
      <Box height='100%' sx={{
        ...sxSumColorClass,
        "& .month-amnt-color": { bgcolor: blue[50] },
        // items not adding up to the header's control total
        "& .amnt-mismatch": { bgcolor: red[50], color: red[800], fontWeight: 'bold' },
      }}>
        <DataGridCellExpand
          hideFooter
          density="compact"
          rows={modDataTable}
          columns={truckFixCostColumns}
          onRowDoubleClick={handleRowDoubleClick}
          onCellClick={handleCellClick}
          getRowClassName={getSumColorClassName}
        />
      </Box>
      <TruckFixCostDialog
        selectedId={selectedId}
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        onFinish={onFinish}
        typCombo={typCombo}
      />
    </BoxFC>
  )
}

export default TruckFixCost
